import { useState, useMemo } from 'react'
import { DriverAnalysis, VehicleUsed } from '../types'

interface GroupedVehicle {
  registration_number: string
  registration_nation?: string
  vin?: string
  first_use?: string
  last_use?: string
  odometer_start?: number
  odometer_end?: number
  total_distance: number
  entries: VehicleUsed[]
  daily_entries: { date: string; driving_minutes: number; work_minutes: number; distance_km: number; status: string }[]
}

export function VehiclesTab({ analysis }: { analysis: DriverAnalysis }) {
  const { vehicles_used, daily_breakdown } = analysis
  const [expandedVehicle, setExpandedVehicle] = useState<string | null>(null)

  // Group vehicles by registration
  const grouped = useMemo(() => {
    const map = new Map<string, GroupedVehicle>()

    for (const v of vehicles_used) {
      const reg = v.registration_number
      if (!map.has(reg)) {
        map.set(reg, {
          registration_number: reg,
          registration_nation: v.registration_nation,
          vin: v.vin,
          first_use: v.first_use,
          last_use: v.last_use,
          odometer_start: v.odometer_start,
          odometer_end: v.odometer_end,
          total_distance: 0,
          entries: [],
          daily_entries: [],
        })
      }
      const g = map.get(reg)!
      g.entries.push(v)

      // Expand date range
      if (v.first_use && (!g.first_use || v.first_use < g.first_use)) g.first_use = v.first_use
      if (v.last_use && (!g.last_use || v.last_use > g.last_use)) g.last_use = v.last_use

      // Track overall odometer range
      if (v.odometer_start != null && (g.odometer_start == null || v.odometer_start < g.odometer_start)) g.odometer_start = v.odometer_start
      if (v.odometer_end != null && (g.odometer_end == null || v.odometer_end > g.odometer_end)) g.odometer_end = v.odometer_end

      // Sum distance per entry
      if (v.odometer_end != null && v.odometer_start != null) {
        g.total_distance += Math.max(0, v.odometer_end - v.odometer_start)
      }
    }

    // Gather daily entries per vehicle
    for (const day of daily_breakdown ?? []) {
      const regs = day.vehicles?.map(v => v.reg) ?? []
      for (const reg of regs) {
        const g = map.get(reg)
        if (g) {
          g.daily_entries.push({
            date: day.date,
            driving_minutes: day.driving_minutes,
            work_minutes: day.work_minutes,
            distance_km: day.distance_km,
            status: day.status,
          })
        }
      }
    }

    return Array.from(map.values())
  }, [vehicles_used, daily_breakdown])

  if (vehicles_used.length === 0) {
    return (
      <div className="text-center py-16 text-gray-500">
        <p>No vehicle records found on this card.</p>
      </div>
    )
  }

  function toggleVehicle(reg: string) {
    setExpandedVehicle(prev => prev === reg ? null : reg)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">
          Vehicles Used ({grouped.length} vehicle{grouped.length !== 1 ? 's' : ''}, {vehicles_used.length} record{vehicles_used.length !== 1 ? 's' : ''})
        </h3>
        <p className="text-xs text-gray-400">Click a vehicle to see entries</p>
      </div>

      <div className="border border-gray-200 rounded overflow-hidden">
        {/* Header row */}
        <div className="grid grid-cols-12 gap-2 bg-gray-50 px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
          <div className="col-span-3">Registration</div>
          <div className="col-span-2">First Used</div>
          <div className="col-span-2">Last Used</div>
          <div className="col-span-2">Odometer Range</div>
          <div className="col-span-1 text-center">Entries</div>
          <div className="col-span-2 text-right">Total Distance</div>
        </div>

        {grouped.map((g) => {
          const isExpanded = expandedVehicle === g.registration_number

          return (
            <div key={g.registration_number} className={isExpanded ? 'bg-blue-50/30' : ''}>
              {/* Vehicle summary row */}
              <button
                onClick={() => toggleVehicle(g.registration_number)}
                className={`w-full grid grid-cols-12 gap-2 px-4 py-3 text-sm text-left hover:bg-gray-50 transition-colors border-b border-gray-100 items-center ${
                  isExpanded ? 'bg-blue-50 border-b-blue-200' : ''
                }`}
              >
                <div className="col-span-3 flex items-center gap-2">
                  <svg className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <div className="bg-yellow-300 border border-gray-700 rounded px-2 py-0.5">
                    <span className="font-mono font-bold text-gray-900 text-xs tracking-wider">
                      {g.registration_number}
                    </span>
                  </div>
                  {(g.entries[0] as any)?.is_company_vehicle ? (
                    <span className="inline-block px-1.5 py-0.5 text-[9px] font-semibold rounded bg-blue-100 text-blue-700 border border-blue-200">Company</span>
                  ) : (
                    <span className="inline-block px-1.5 py-0.5 text-[9px] font-semibold rounded bg-gray-100 text-gray-500 border border-gray-200">Other</span>
                  )}
                  {g.registration_nation && (
                    <span className="text-xs text-gray-400">{g.registration_nation}</span>
                  )}
                </div>
                <div className="col-span-2 text-gray-600">
                  {g.first_use ? new Date(g.first_use).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                </div>
                <div className="col-span-2 text-gray-600">
                  {g.last_use ? new Date(g.last_use).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                </div>
                <div className="col-span-2 text-gray-600 text-xs">
                  {g.odometer_start != null && g.odometer_end != null
                    ? `${g.odometer_start.toLocaleString()} → ${g.odometer_end.toLocaleString()} km`
                    : '—'}
                </div>
                <div className="col-span-1 text-center">
                  {g.entries.length > 1 ? (
                    <span className="inline-block px-2 py-0.5 bg-slate-100 text-blue-700 rounded text-xs font-medium">
                      {g.entries.length}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">1</span>
                  )}
                </div>
                <div className="col-span-2 text-right">
                  {g.total_distance > 0 ? (
                    <span className="font-bold text-blue-700">{g.total_distance.toLocaleString()} km</span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </div>
              </button>

              {/* Expanded: individual entries + daily breakdown */}
              {isExpanded && (
                <div className="px-4 py-3 border-b border-gray-200 space-y-4">
                  {/* Individual card entries for this vehicle */}
                  {g.entries.length > 1 && (
                    <div className="ml-6">
                      <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Card Entries</p>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-gray-400 uppercase">
                            <th className="text-left py-1 pr-4">From</th>
                            <th className="text-left py-1 pr-4">To</th>
                            <th className="text-right py-1 pr-4">Odo Start</th>
                            <th className="text-right py-1 pr-4">Odo End</th>
                            <th className="text-right py-1">Distance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.entries.map((e, j) => {
                            const dist = (e.odometer_end != null && e.odometer_start != null) ? Math.max(0, e.odometer_end - e.odometer_start) : null
                            return (
                              <tr key={j} className="border-t border-gray-100">
                                <td className="py-1.5 pr-4 text-gray-700">
                                  {e.first_use ? new Date(e.first_use).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                </td>
                                <td className="py-1.5 pr-4 text-gray-700">
                                  {e.last_use ? new Date(e.last_use).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                </td>
                                <td className="py-1.5 pr-4 text-right text-gray-600 font-mono text-xs">
                                  {e.odometer_start != null ? `${e.odometer_start.toLocaleString()} km` : '—'}
                                </td>
                                <td className="py-1.5 pr-4 text-right text-gray-600 font-mono text-xs">
                                  {e.odometer_end != null ? `${e.odometer_end.toLocaleString()} km` : '—'}
                                </td>
                                <td className="py-1.5 text-right font-medium text-blue-700">
                                  {dist != null && dist > 0 ? `${dist.toLocaleString()} km` : '—'}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* VIN if available */}
                  {g.vin && (
                    <div className="ml-6">
                      <span className="text-xs text-gray-400">VIN: </span>
                      <span className="text-xs text-gray-600 font-mono">{g.vin}</span>
                    </div>
                  )}

                  {/* Daily activity for this vehicle */}
                  {g.daily_entries.length > 0 && (
                    <div className="ml-6">
                      <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Daily Activity</p>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-gray-400 uppercase">
                            <th className="text-left py-1 pr-4">Date</th>
                            <th className="text-left py-1 pr-4">Day</th>
                            <th className="text-right py-1 pr-4">Driving</th>
                            <th className="text-right py-1 pr-4">Work</th>
                            <th className="text-right py-1 pr-4">Distance</th>
                            <th className="text-right py-1">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.daily_entries.map((day, j) => {
                            const dow = new Date(day.date).toLocaleDateString('en-GB', { weekday: 'short' })
                            return (
                              <tr key={j} className="border-t border-gray-100">
                                <td className="py-1.5 pr-4 text-gray-700">{new Date(day.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</td>
                                <td className="py-1.5 pr-4 text-gray-500">{dow}</td>
                                <td className="py-1.5 pr-4 text-right text-gray-700">{(day.driving_minutes / 60).toFixed(1)}h</td>
                                <td className="py-1.5 pr-4 text-right text-gray-700">{(day.work_minutes / 60).toFixed(1)}h</td>
                                <td className="py-1.5 pr-4 text-right text-gray-700">{day.distance_km > 0 ? `${day.distance_km} km` : '—'}</td>
                                <td className="py-1.5 text-right">
                                  <span className={`inline-block w-2 h-2 rounded-full ${
                                    day.status === 'ok' ? 'bg-green-500' :
                                    day.status === 'warning' ? 'bg-amber-500' :
                                    'bg-red-500'
                                  }`} />
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                      <div className="mt-2 pt-2 border-t border-gray-100 flex gap-6 text-xs text-gray-500">
                        <span>Total driving: <strong className="text-gray-700">{(g.daily_entries.reduce((s, d) => s + d.driving_minutes, 0) / 60).toFixed(1)}h</strong></span>
                        <span>Total distance: <strong className="text-gray-700">{g.daily_entries.reduce((s, d) => s + d.distance_km, 0).toLocaleString()} km</strong></span>
                        <span>Days: <strong className="text-gray-700">{g.daily_entries.length}</strong></span>
                      </div>
                    </div>
                  )}

                  {g.daily_entries.length === 0 && g.entries.length <= 1 && (
                    <p className="ml-6 text-xs text-gray-400">No additional detail available for this vehicle.</p>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
